import { useEffect, useRef, useState } from 'react'
import {
  Container,
  BarGraph,
  Columns,
  ScaleY,
  ChartArea,
  AreaBars,
  ScaleX,
  BarsY,
  Performance,
  Days,
  HoursY,
  Floating,
  FloatingArrow,
  StudyGoalLine,
  ShadowArea,
} from './styles'

import gsap from 'gsap'
import Draggable from 'gsap/Draggable'
import { useMount } from 'react-use'
import rectangle from '../../../../assets/icons/rectangle.svg'
import { IDashboardCourse, IStudyTime } from '../DashboardContent/types'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store/modules/rootReducer'
import { TTrackingEvents } from '../../hooks/use-dashboard-tracking'
import { PreferenceChangeGoal } from '../../../../store/modules/dashboard/action'
import { DASHBOARD_PREFERENCE } from 'pages/Dashboard/services'

const horasBase = [
  { id: 0, totalHours: 24 },
  { id: 1, totalHours: 18 },
  { id: 2, totalHours: 12 },
  { id: 3, totalHours: 6 },
  { id: 4, totalHours: 0 },
]

gsap.registerPlugin(Draggable)

interface IPerformanceProps {
  timeStudy: IStudyTime
  course: IDashboardCourse
  tracking: TTrackingEvents
}

const TempoEstudo = (props: IPerformanceProps) => {
  const { hoursByDay: days } = props.timeStudy
  const { student, preferences } = useSelector((state: RootState) => ({
    student: state.student,
    preferences: state.dashboard?.preferences,
  }))
  const dispatch = useDispatch()

  const hoursGoalPreference = preferences.data?.courses[props.course._id]?.hoursGoal || 0
  const [selectedHours, setSelectedHours] = useState<number>(hoursGoalPreference)
  function moveLine(hoursGoal: number) {
    const [line, lineBounds] = [lineRef.current, lineBoundsRef.current]

    const snap = lineBounds.clientHeight / 48

    gsap.to(line, { y: hoursGoal * snap * -2 })
  }

  useEffect(() => {
    setSelectedHours(hoursGoalPreference)

    moveLine(hoursGoalPreference)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.course._id])

  const lineRef = useRef<HTMLDivElement>(null)
  const lineBoundsRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<HTMLDivElement>(null)

  useMount(() => {
    const [line, lineBounds, shadow] = [lineRef.current, lineBoundsRef.current, shadowRef.current]
    let localSelectedHours = 0

    const label = line.querySelector('.label') as HTMLDivElement

    const snap = lineBounds.clientHeight / 48

    function onPress() {
      line.style.borderStyle = 'solid'
      label.style.borderStyle = 'solid'
      label.style.opacity = '1'
      shadow.style.opacity = '1'
    }
    function onRelease() {
      line.style.borderStyle = 'dashed'
      label.style.borderStyle = 'dashed'
      label.style.opacity = '0'
      shadow.style.opacity = '0'
      dispatch(
        PreferenceChangeGoal({
          hoursGoal: localSelectedHours,
          courseId: props.course._id,
        })
      )
      postPreference(localSelectedHours)

      props.tracking.onStudyGoalChange({
        courseId: props.course._id,
        courseName: props.course.name,
        studyGoal: localSelectedHours,
      })
    }

    Draggable.create(line, {
      bounds: lineBounds,
      type: 'y',
      zIndexBoost: false,
      liveSnap: {
        y(value) {
          const snapped = Math.round(value / snap) * snap

          const time = Math.round(Math.abs(value) / snap) / 2

          const hours = Math.floor(time)
          const minutes = (time - hours) * 60

          label.textContent = `${hours}h${minutes > 0 ? minutes : ''}`
          shadow.style.height = `${(time / 24) * 100}%`

          localSelectedHours = time

          setSelectedHours(time)
          return snapped
        },
      },
      onPress,
      onRelease,
    })
  })

  function postPreference(localSelectedHours: number) {
    try {
      const studentInfo = {
        _id: student.profile._id,
        name: student.profile.name,
      }

      const courseInfo = {
        _id: props.course._id,
        name: props.course.name,
      }

      DASHBOARD_PREFERENCE(localSelectedHours, studentInfo, courseInfo)
    } catch (error) {
      console.log(error)
    }
  }

  const RenderHorasY = () =>
    horasBase.map((test) => (
      <HoursY hours={test.totalHours} key={test.id}>
        <div className="label">{test.totalHours}h</div>
      </HoursY>
    ))

  function getUTCDate(date: string) {
    const dateStudent = new Date(date)
    return new Date(dateStudent.getTime() + Math.abs(dateStudent.getTimezoneOffset() * 60000))
  }

  function getHourProgress(progress: string) {
    const hour = Number(progress.split('.')[0])
    const minute = `${Number(progress.split('.')[1]) * 0.6}`

    const valueProgress = `${hour}:${minute.split('.')[0]}`
    return valueProgress
  }

  function getGoal(goal: number) {
    const hours = Math.floor(goal)
    const minutes = (goal - hours) * 60

    const minutesString = minutes > 0 ? `${minutes}m` : ''

    const timeString = `${hours}h${minutesString}`

    return timeString
  }
  return (
    <Container id="container">
      <BarGraph>
        <Columns>
          <ScaleY>{RenderHorasY()}</ScaleY>
          <ChartArea>
            <div className="chartLines">
              <div className="line" />
              <div className="line" />
              <div className="line" />
              <div className="line" />
              <div className="line" />
            </div>
            <AreaBars className="grafico" ref={lineBoundsRef}>
              <ShadowArea ref={shadowRef} id="area" />
              <StudyGoalLine ref={lineRef}>
                <div className="label">{getGoal(selectedHours)}</div>
                <img src={rectangle} alt={'arrow'} />
                {hoursGoalPreference === 0 ? (
                  <FloatingArrow horizontalOffset={0}>
                    Clique e arraste para definir uma meta de estudos
                  </FloatingArrow>
                ) : null}
              </StudyGoalLine>
              {days.map((day, idx) => (
                <BarsY key={idx}>
                  <Performance hours={day.totalHours} completed={day.totalHours >= selectedHours}>
                    <Floating horizontalOffset={0}>
                      Seu tempo total de estudos foi {getHourProgress(day.totalHours.toFixed(2))}
                    </Floating>
                  </Performance>
                </BarsY>
              ))}
            </AreaBars>
            <ScaleX>
              {days.map((item, idx) => (
                <Days key={idx}>
                  <hr />
                  <div className="mobile">
                    {getUTCDate(item.day)
                      .toLocaleDateString('pt-br', {
                        weekday: 'long',
                      })
                      .slice(0, 1)}
                  </div>
                  <div className="desk">
                    {getUTCDate(item.day)
                      .toLocaleDateString('pt-br', {
                        weekday: 'long',
                      })
                      .slice(0, 3)}
                  </div>
                  <div className="dataStudy">
                    {getUTCDate(item.day).toLocaleDateString('pt-br', {
                      day: '2-digit',
                      month: '2-digit',
                    })}
                  </div>
                </Days>
              ))}
            </ScaleX>
          </ChartArea>
        </Columns>
      </BarGraph>
    </Container>
  )
}

export default TempoEstudo
